import type { AxiosError } from 'axios';

export type ApiFieldErrors = Record<string, string[]>;

export class ApiError extends Error {
  readonly status: number | null;
  readonly errors: ApiFieldErrors | null;
  readonly isNetwork: boolean;
  /** Correlation id — the `X-Request-ID` header we sent (or the server
   *  echoed back). Included in error toasts + Sentry breadcrumbs so ops
   *  can grep the Laravel log for a specific request. */
  readonly requestId: string | null;

  constructor(opts: {
    message: string;
    status: number | null;
    errors?: ApiFieldErrors | null;
    isNetwork?: boolean;
    requestId?: string | null;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.errors = opts.errors ?? null;
    this.isNetwork = opts.isNetwork ?? false;
    this.requestId = opts.requestId ?? null;
  }
}

/** Backend `message` strings we want to map to friendlier copy.
 *  Populated as modules land — empty for Milestone 1. */
const COPY_DICTIONARY: Record<string, string> = {};

interface LaravelErrorPayload {
  message?: string;
  errors?: ApiFieldErrors;
  /** Present on the 5xx envelope shipped 2026-07-07. Trumps every other
   *  source because it's the id Laravel actually logged. */
  request_id?: string;
}

/** Grab the correlation id from every source we know of. Order (highest
 *  precedence first): body.request_id > response header > sent header. */
function extractRequestId(err: AxiosError): string | null {
  const body = (err.response?.data ?? {}) as LaravelErrorPayload;
  if (typeof body.request_id === 'string' && body.request_id) return body.request_id;
  const responseId = (err.response?.headers?.['x-request-id'] as string | undefined) ?? null;
  if (responseId) return responseId;
  const sent = err.config?.headers?.['X-Request-ID'];
  return typeof sent === 'string' ? sent : null;
}

export function toApiError(err: AxiosError): ApiError {
  const requestId = extractRequestId(err);

  if (err.response) {
    const { status, data } = err.response;
    const payload = (data ?? {}) as LaravelErrorPayload;
    const raw = payload.message ?? err.message ?? 'Request failed';
    return new ApiError({
      message: COPY_DICTIONARY[raw] ?? raw,
      status,
      errors: payload.errors ?? null,
      requestId,
    });
  }

  if (err.request) {
    return new ApiError({
      message: 'Network error — check your connection.',
      status: null,
      isNetwork: true,
      requestId,
    });
  }

  return new ApiError({
    message: err.message ?? 'Unknown error',
    status: null,
    requestId,
  });
}
